import React, { useState } from 'react';
import { Heart, CheckCircle2, HandHeart } from 'lucide-react';
import { PageHeader } from './MassTimes';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';
import { HoneypotField } from '../components/HoneypotField';

const PrayerRequest = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', intention: '', website: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.createPrayer(form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast({ title: 'Could not send', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <>
        <PageHeader eyebrow="Received" title="Your Intention Has Been Received" subtitle="You are being prayed for." />
        <section className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#f2ead6] text-[#3a2359] flex items-center justify-center mb-6">
            <CheckCircle2 size={30} />
          </div>
          <h2 className="font-display text-3xl text-[#2a1a45] mb-4">Thank you for entrusting us with your intention</h2>
          <p className="text-[#5a534a] leading-relaxed mb-6">
            Your prayer request has been received. It will be shared with the parishioners of our prayer chain ministry, and may be included in the prayers of the faithful at Mass in the weeks ahead.
          </p>
          <p className="text-sm text-[#78716c]">
            &ldquo;Ask, and it will be given to you; seek, and you will find; knock, and it will be opened to you.&rdquo;
            <span className="block mt-1">— Matthew 7:7</span>
          </p>
          <button
            onClick={() => { setSubmitted(false); setForm({ name: '', email: '', intention: '' }); }}
            className="mt-8 text-sm underline text-[#3a2359]"
          >
            Submit another intention
          </button>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="A Prayerful Community"
        title="Prayer Requests"
        subtitle="Share your intentions with our parish community — your request will be prayed for by our prayer chain ministry and may be lifted up at Mass."
      />

      <section className="max-w-2xl mx-auto px-6 py-14">
        {/* Sharing notice */}
        <div className="bg-[#f2ead6]/50 border border-[#e9e0cc] p-5 mb-8 flex items-start gap-3">
          <HandHeart size={18} className="text-[#a68a5b] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-[#3f3a34] leading-relaxed">
            Prayer requests submitted here are <span className="font-medium">not confidential</span>. Your intention will be shared with the parishioners of our prayer chain ministry and may be included in the prayers of the faithful at Mass. Name and email are optional — you may submit anonymously if you prefer.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-[#e9e0cc] p-8 space-y-5">
          <HoneypotField value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-[#f2ead6] text-[#3a2359] flex items-center justify-center">
              <Heart size={18} />
            </div>
            <h2 className="font-display text-2xl text-[#2a1a45]">Share Your Intention</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Your Name <span className="normal-case tracking-normal text-[#a68a5b] text-[10px]">(optional)</span></label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Email <span className="normal-case tracking-normal text-[#a68a5b] text-[10px]">(optional)</span></label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="For a follow-up, if you wish"
                className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Your Prayer Intention <span className="text-[#a68a5b]">*</span></label>
            <textarea
              required
              rows={6}
              value={form.intention}
              onChange={e => setForm({ ...form, intention: e.target.value })}
              placeholder="Please pray for..."
              className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || !form.intention.trim()}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#3a2359] text-white text-sm tracking-wide hover:bg-[#4d2f75] disabled:opacity-60"
          >
            <Heart size={16} /> {submitting ? 'Sending…' : 'Submit Prayer Request'}
          </button>
        </form>
      </section>
    </>
  );
};

export default PrayerRequest;
